'use client';

import React from 'react';
import { Flex } from '@skin-seoul/design-system';
import { motion, AnimatePresence } from 'motion/react';
import MotionLoader from './MotionLoader';
const MotionFlex = motion.create(Flex);
interface MotionLoaderBackdropProps {
  /** Whether the loader is visible */
  isVisible: boolean;
  /** Loading message to display */
  message?: string;
  /** Size of the loader */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Color scheme for the loader */
  colorScheme?: 'primary' | 'neutral' | 'brand';
  /** Whether to show the loading message */
  showMessage?: boolean;
  /** Test ID for testing purposes */
  'data-testid'?: string;
}

/**
 * MotionLoaderBackdrop component for full-screen loading states
 *
 * Features:
 * - Full-screen overlay with backdrop blur
 * - Smooth fade in/out animations
 * - Customizable loader appearance
 * - Prevents user interaction during loading
 *
 * @example
 * ```tsx
 * <MotionLoaderBackdrop
 *   isVisible={isLoading}
 *   message="Processing payment..."
 *   colorScheme="primary"
 *   size="lg"
 * />
 * ```
 */
const MotionLoaderBackdrop = ({
  isVisible,
  message = 'Loading...',
  size = 'lg',
  colorScheme = 'primary',
  showMessage = true,
  'data-testid': testId
}: MotionLoaderBackdropProps) => {
  return <AnimatePresence mode="wait" data-sentry-element="AnimatePresence" data-sentry-component="MotionLoaderBackdrop" data-sentry-source-file="MotionLoaderBackdrop.tsx">
      {isVisible && <MotionFlex position="fixed" top={0} left={0} right={0} bottom={0} backgroundColor="rgba(0, 0, 0, 0.03)" backdropFilter="blur(4px)" zIndex="overlay" alignItems="center" justifyContent="center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} data-testid={testId}>
          <MotionLoader message={message} size={size} colorScheme={colorScheme} showMessage={showMessage} />
        </MotionFlex>}
    </AnimatePresence>;
};
export default MotionLoaderBackdrop;